import {
  line as lineD3,
  area as areaD3,
  curveLinear,
  max,
  min,
  select,
  zoomTransform,
  event,
  format,
  timeFormat,
  zoom,
  curveMonotoneX,
  selectAll,
  bisector,
  axisRight,
} from "d3";
import {
  candleCreate,
  dotCreate,
  hLineCreate,
  vLineCreate,
} from "@/_core/charts/D3ChartHoverHelpers";
import {
  colorDarkExpo,
  grayLightBackground,
  lightbackground2,
  textactive,
  textactive2,
} from "@/_core/_helpers/_parametric_tools";
import { trendLine, getTrendLineData, filterTrendLineData } from "./TrendLine";
import {
  darkBackground,
  trendsColorSelection,
} from "../_helpers/_parametric_tools";

export function TACandles(
  svg,
  data,
  upColor,
  dnColor,
  customName,
  opacityUp = 1,
  opacityDn = 1,
  upColorBackground = null,
  dnColorBackground = null,
  lineWidth = 1,
  idField = "id",
  fieldStick1 = "stick",
  fieldCandle = "candle",
  svgAux = null,
  candleWidth,
) {
  if (svgAux === null) {
    svgAux = svg;
  }
  if (upColorBackground === null) {
    upColorBackground = upColor;
  }
  if (dnColorBackground === null) {
    dnColorBackground = dnColor;
  }

  let newData = dataForSticks(data);
  var line = lineD3()
    .curve(curveLinear)
    .defined((d) => !Number.isNaN(d.y))
    .x((d) => svg.x(d.date))
    .y((d) => svg.y(d.y));

  svg[fieldStick1] = svgAux
    .append("g")
    .attr("class", "sticks" + customName)
    .attr("fill", "none")
    .attr("stroke-linejoin", "round")
    .attr("stroke-linecap", "round")
    .selectAll("path")
    .data(newData.series, (d) => d.id)
    .join("path")
    .style("stroke", (d) => (d.name === "up" ? upColor : dnColor))
    .style("stroke-width", lineWidth)
    .attr("d", (d) => line(d.values))
    .style("shape-rendering", "crispEdges")
    .style("vector-effect", "non-scaling-stroke");

  // add candle bodies
  svg[fieldCandle] = svgAux
    .selectAll(".candles" + customName)
    .data(data, (d) => d[idField])
    .enter()
    .append("rect")
    .attr("x", function(d) {
      return svg.x(d.date) - candleWidth / 2;
    })
    .attr("y", function(d) {
      return svg.y(max([d.open, d.close]));
    })
    .attr("height", function(d) {
      return Math.max(
        1,
        svg.y(min([d.open, d.close])) - svg.y(max([d.open, d.close])),
      );
    })
    .attr("width", candleWidth)
    .attr("stroke-width", lineWidth)
    .attr("stroke", function(d) {
      return d.close >= d.open ? upColor : dnColor;
    })
    .style("fill", function(d) {
      return d.close >= d.open ? upColorBackground : dnColorBackground;
    })
    .attr("fill-opacity", function(d) {
      return d.close >= d.open ? opacityUp : opacityDn;
    })
    .style("shape-rendering", "crispEdges")
    .style("vector-effect", "non-scaling-stroke");

  return svg;
}

export function TABars(
  svg,
  data,
  customName,
  yField = "value",
  fieldColor,
  fieldBackground = null,
  lineWidth = 1,
  idField = "id",
  svgAux = null,
  candleWidth,
  toZero = true,
) {
  if (svgAux === null) {
    svgAux = svg;
  }
  svg[customName] = svgAux
    .selectAll(".bars" + customName)
    .data(data, (d) => d[idField])
    .enter()
    .append("rect")
    .attr("x", function(d) {
      return svg.x(d.date) - candleWidth / 2;
    })
    .attr("y", function(d) {
      return toZero === true
        ? Math.min(svg.y(0), svg.y(d[yField]))
        : svg.y(d[yField]) - 1;
    })
    .attr("class", function(d) {
      return "customName" + customName + "" + d[idField];
    })
    .attr("height", function(d) {
      return Math.max(
        1,
        toZero === true ? Math.abs(svg.y(0) - svg.y(d[yField])) : 2,
      );
    })
    .attr("width", candleWidth)
    .attr("stroke-width", lineWidth)
    .attr("stroke", function(d) {
      return d[fieldColor];
    })
    .style("fill", function(d) {
      return d[fieldBackground];
    })
    .style("shape-rendering", "crispEdges")
    .style("vector-effect", "non-scaling-stroke");

  return svg;
}

export function TAHover(
  _this,
  chartBody,
  upColor,
  dnColor,
  createTrendLine,
  opacityUp = 1,
  opacityDn = 1,
  upColorBackground = null,
  dnColorBackground = null,
  barWidth = 0.7,
  colorTheme = true,
) {
  if (upColorBackground === null) {
    upColorBackground = upColor;
  }
  if (dnColorBackground === null) {
    dnColorBackground = dnColor;
  }
  const auxName = "TA";
  let valueLast = _this.candleData[_this.candleData.length - 1].close;
  let xTooltipWidth = 80;
  let yTooltip = createTooltips(
    _this.svgNode,
    _this.currencyFormat,
    valueLast,
    xTooltipWidth,
    colorTheme,
    auxName,
  );
  let lastYTooltip = yTooltip["lastTooltip"];
  _this.svgNode.lastYTooltip = lastYTooltip;
  let xTooltip = yTooltip["xTooltip"];
  yTooltip = yTooltip["yTooltip"];
  hLineCreate(
    _this.svgNode,
    auxName + _this.chartName,
    grayLightBackground(colorTheme),
    1,
  );
  vLineCreate(
    _this.svgNode,
    auxName + _this.chartName,
    grayLightBackground(colorTheme),
    1,
  );
  candleCreate(
    _this.svgNode,
    auxName + _this.chartName,
    textactive(colorTheme),
    3.5,
    chartBody,
  );
  dotCreate(
    _this.svgNode,
    auxName + _this.chartName,
    2,
    textactive2(colorTheme),
    upColor,
    1.5,
  );

  function mouseover() {
    if (_this.transform === undefined || _this.transform == null) {
      _this.transform = zoomTransform(
        select(".hoverParent" + auxName + _this.chartName)._groups[0][0],
      );
    }
    event.preventDefault();
    const xPixels = Math.max(
      Math.min(event.layerX - _this.svgNode.margin.left, _this.svgNode.width),
      0,
    );
    var xt = _this.transform.rescaleX(_this.svgNode.x);
    var yt = _this.svgNode.y;
    let dat = roundDate(xt.invert(xPixels));
    let selData;

    let highlightCandles = true;
    if (dat <= _this.candleData[_this.candleData.length - 1].date) {
      selData = mouseDate(xt.invert(xPixels), _this.candleData);
    } else {
      selData = JSON.parse(
        JSON.stringify(_this.candleData[_this.candleData.length - 1]),
      );
      selData["date"] = dat;
      highlightCandles = false;
    }
    const yPixels = Math.max(
      Math.min(event.layerY - _this.svgNode.margin.top, _this.svgNode.height),
      0,
    );
    let yRate = yt.invert(yPixels);
    yTooltip
      .style("opacity", 1)
      .style("left", _this.svgNode.width + _this.svgNode.margin.left + 1 + "px")
      .style("top", yPixels + _this.svgNode.margin.top - 11 + "px")
      .html(format(_this.currencyFormat)(yRate));

    xTooltip
      .style("opacity", 1)
      .style(
        "left",
        xt(selData.date) + _this.svgNode.margin.left - xTooltipWidth / 2 + "px",
      )
      .style("top", _this.svgNode.height + _this.svgNode.margin.top + 2 + "px")
      .html(timeFormat(_this.dateFormat)(selData.date));

    select(".hHoverLineTA" + _this.chartName)
      .style("opacity", 1)
      .attr("y1", yPixels)
      .attr("y2", yPixels);

    let xPix = xt(selData.date);

    select(".vHoverLineTA" + _this.chartName)
      .style("opacity", 1)
      .attr("x1", xPix)
      .attr("x2", xPix);
    if (_this.showRSI) {
      select(".vHoverLineRSI" + _this.chartName)
        .style("opacity", 1)
        .attr("x1", xPix)
        .attr("x2", xPix);
    }
    if (_this.showMACD === true) {
      select(".vHoverLineMACD" + _this.chartName)
        .style("opacity", 1)
        .attr("x1", xPix)
        .attr("x2", xPix);
    }
    if (_this.showSTOCH === true) {
      select(".vHoverLineSTOCH" + _this.chartName)
        .style("opacity", 1)
        .attr("x1", xPix)
        .attr("x2", xPix);
    }

    if (highlightCandles) {
      let bandWidth = _this.transform.k * _this.bandWidth;
      const candleWidth = bandWidth * _this.barWidth + 1;
      if (_this.isCandles) {
        let up = Math.max(selData.open, selData.close);
        let dn = Math.min(selData.open, selData.close);
        select(".cndlStckTA" + _this.chartName)
          .style("opacity", 1)
          .attr("x1", xPix)
          .attr("x2", xPix)
          .attr("y1", yt(selData.high))
          .attr("y2", yt(up));
        select(".cndlStck2TA" + _this.chartName)
          .style("opacity", 1)
          .attr("x1", xPix)
          .attr("x2", xPix)
          .attr("y1", yt(dn))
          .attr("y2", yt(selData.low));
        select(".cndlTA" + _this.chartName)
          .style("opacity", 1)
          .attr("x", xPix - candleWidth / 2)
          .attr("y", yt(up))
          .attr("height", Math.max(1, yt(dn) - yt(up)))
          .attr("width", candleWidth);
      } else {
        select(".dotTA" + _this.chartName)
          .style("opacity", 1)
          .attr(
            "transform",
            `translate(${xt(selData.date)},${yt(selData.close)})`,
          );
      }
      if (_this.showRSI) {
        const yt2 = _this.svgNodeRSI.y;
        select(".dotRSI" + _this.chartName)
          .style("opacity", 1)
          .attr(
            "transform",
            `translate(${xt(selData.date)},${yt2(selData.rsi)})`,
          );
      }
      if (_this.showMACD === true) {
        const yt3 = _this.svgNodeMACD.y;
        select(".dotMACD" + _this.chartName)
          .style("opacity", 1)
          .attr(
            "transform",
            `translate(${xt(selData.date)},${yt3(selData.macd)})`,
          );
        select(".dotMACDSignal" + _this.chartName)
          .style("opacity", 1)
          .attr(
            "transform",
            `translate(${xt(selData.date)},${yt3(selData.macdSignal)})`,
          );
        select(".barMACDHistogram" + _this.chartName)
          .style("opacity", 1)
          .attr("x", xPix - candleWidth / 2)
          .attr("y", Math.min(yt3(0), yt3(selData["macdHist"])))
          .attr(
            "height",
            Math.max(1, Math.abs(yt3(0) - yt3(selData["macdHist"]))),
          )
          .attr("width", candleWidth);
      }
      if (_this.showSTOCH === true) {
        const yt4 = _this.svgNodeSTOCH.y;
        select(".dotStochPK" + _this.chartName)
          .style("opacity", 1)
          .attr(
            "transform",
            `translate(${xt(selData.date)},${yt4(selData.stochPK)})`,
          );
        select(".dotStochPD" + _this.chartName)
          .style("opacity", 1)
          .attr(
            "transform",
            `translate(${xt(selData.date)},${yt4(selData.stochPD)})`,
          );
      }
    } else {
      if (_this.isCandles) {
        select(".cndlStckTA" + _this.chartName).style("opacity", 0);
        select(".cndlStck2TA" + _this.chartName).style("opacity", 0);
        select(".cndlTA" + _this.chartName).style("opacity", 0);
      } else {
        select(".dotTA" + _this.chartName).style("opacity", 0);
      }
      if (_this.showRSI) {
        select(".dotRSI" + _this.chartName).style("opacity", 0);
      }
      if (_this.showMACD === true) {
        select(".barMACDHistogram" + _this.chartName).style("opacity", 0);
        select(".dotMACD" + _this.chartName).style("opacity", 0);
        select(".dotMACDSignal" + _this.chartName).style("opacity", 0);
      }
      if (_this.showSTOCH === true) {
        select(".dotStochPK" + _this.chartName).style("opacity", 0);
        select(".dotStochPD" + _this.chartName).style("opacity", 0);
      }
    }

    displayDataTA(selData, _this);
  }
  function mouseout() {
    yTooltip
      .style("left", "-1000000px")
      .style("top", "0px")
      .style("opacity", 0)
      .html("");
    xTooltip
      .style("left", "-1000000px")
      .style("top", "0px")
      .style("opacity", 0)
      .html("");
    select(".hHoverLineTA" + _this.chartName).style("opacity", 0);
    select(".vHoverLineTA" + _this.chartName).style("opacity", 0);
    if (_this.isCandles) {
      select(".cndlStckTA" + _this.chartName).style("opacity", 0);
      select(".cndlStck2TA" + _this.chartName).style("opacity", 0);
      select(".cndlTA" + _this.chartName).style("opacity", 0);
    } else {
      select(".dotTA" + _this.chartName).style("opacity", 0);
    }
    if (_this.showRSI) {
      select(".vHoverLineRSI" + _this.chartName).style("opacity", 0);
      select(".dotRSI" + _this.chartName).style("opacity", 0);
    }
    if (_this.showMACD === true) {
      select(".barMACDHistogram" + _this.chartName).style("opacity", 0);
      select(".vHoverLineMACD" + _this.chartName).style("opacity", 0);
      select(".dotMACD" + _this.chartName).style("opacity", 0);
      select(".dotMACDSignal" + _this.chartName).style("opacity", 0);
    }
    if (_this.showSTOCH === true) {
      select(".vHoverLineSTOCH" + _this.chartName).style("opacity", 0);
      select(".dotStochPK" + _this.chartName).style("opacity", 0);
      select(".dotStochPD" + _this.chartName).style("opacity", 0);
    }
    displayDataTA(_this.candleData[_this.candleData.length - 1], _this);
  }
  function zoomed() {
    if (!_this.keepDateInterval) {
      setTimeout(() => {
        _this.pickedDateInterval = "-1";
      }, 500);
    }
    yTooltip
      .style("left", "-1000000px")
      .style("top", "0px")
      .style("opacity", 0)
      .html("");
    xTooltip
      .style("left", "-1000000px")
      .style("top", "0px")
      .style("opacity", 0)
      .html("");

    select(".hHoverLineTA" + _this.chartName).style("opacity", 0);
    select(".vHoverLineTA" + _this.chartName).style("opacity", 0);
    if (_this.isCandles) {
      select(".cndlStckTA" + _this.chartName).style("opacity", 0);
      select(".cndlStck2TA" + _this.chartName).style("opacity", 0);
      select(".cndlTA" + _this.chartName).style("opacity", 0);
    } else {
      select(".dotTA" + _this.chartName).style("opacity", 0);
    }
    if (_this.showRSI) {
      select(".vHoverLineRSI" + _this.chartName).style("opacity", 0);
      select(".dotRSI" + _this.chartName).style("opacity", 0);
    }
    if (_this.showMACD === true) {
      select(".barMACDHistogram" + _this.chartName).style("opacity", 0);
      select(".vHoverLineMACD" + _this.chartName).style("opacity", 0);
      select(".dotMACD" + _this.chartName).style("opacity", 0);
      select(".dotMACDSignal" + _this.chartName).style("opacity", 0);
    }
    if (_this.showSTOCH === true) {
      select(".vHoverLineSTOCH" + _this.chartName).style("opacity", 0);
      select(".dotStochPK" + _this.chartName).style("opacity", 0);
      select(".dotStochPD" + _this.chartName).style("opacity", 0);
    }

    _this.transform = event.transform;
    _this.svgNode.gX.call(
      _this.svgNode.xAxis.scale(_this.transform.rescaleX(_this.svgNode.x)),
    );
    const xt = _this.transform.rescaleX(_this.svgNode.x); //, yt = t.rescaleY(_this.svgNode.y);
    TACandlesZooming(_this.transform, xt, _this, barWidth);

    // transform other charts
    if (_this.externalZoom === false) {
      _this.externalZoom = true;
      if (_this.showRSI) {
        select(".hoverParentRSI" + _this.chartName).call(
          _this.zoomRSI.transform,
          _this.transform,
        );
      }
      if (_this.showMACD) {
        select(".hoverParentMACD" + _this.chartName).call(
          _this.zoomMACD.transform,
          _this.transform,
        );
      }
      if (_this.showSTOCH) {
        select(".hoverParentSTOCH" + _this.chartName).call(
          _this.zoomSTOCH.transform,
          _this.transform,
        );
      }
      _this.externalZoom = false;
    }
    _this.keepDateInterval = false;
  }
  function zoomEnd() {
    _this.transform = event.transform;
    const xt = _this.transform.rescaleX(_this.svgNode.x);
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function() {
      var xmin = new Date(xt.invert(0));
      var xmax = new Date(xt.invert(_this.svgNode.width));

      if (!_this.externalZoom) {
        let data = _this.candleData.map((d) => d);
        _this.filteredData = data.filter(function(d) {
          return inRange(d.date, xmin, xmax);
        });
      }

      _this.minMaxYAxis = _this.getMinMax(_this.filteredData);
      if (!isFinite(_this.minMaxYAxis[0])) {
        _this.minMaxYAxis[0] = _this.minMaxYAxisOld[0];
      }
      if (!isFinite(_this.minMaxYAxis[1])) {
        _this.minMaxYAxis[1] = _this.minMaxYAxisOld[1];
      }
      _this.minMaxYAxisOld = JSON.parse(JSON.stringify(_this.minMaxYAxis));
      _this.svgNode.y.domain(_this.minMaxYAxis);
      let yt = _this.svgNode.y;

      TACandlesZoomEnd(
        _this.transform,
        xt,
        yt,
        _this.filteredData,
        _this,
        valueLast,
        lastYTooltip,
        upColor,
        dnColor,
        opacityUp,
        opacityDn,
        upColorBackground,
        dnColorBackground,
        barWidth,
      );
    }, 200);
  }

  var resizeTimer;
  zoomSetup(
    _this,
    "svgNode",
    "zoom" + auxName,
    auxName,
    _this.chartName,
    mouseover,
    mouseout,
    zoomed,
    zoomEnd,
  );
  /* Creates the trend line on click */
  trendLine(
    _this,
    createTrendLine,
    trendsColorSelection,
    darkBackground(_this.colorTheme),
    _this.chartName,
  );
  /* Draw the trend lines of the DB */
  getTrendLineData(_this, darkBackground(_this.colorTheme));
  /* Deletes the trend line from the keyboard */
  select("body").on("keydown", function() {
    const gTrendLine = select(".g-trend.selected");

    if (event.key === "Backspace" && gTrendLine.size() > 0) {
      const circleClass = gTrendLine.attr("class");
      const regExp = /[0-9]/g;
      const lastIndex = [...circleClass.matchAll(regExp)].length;
      const id = Number(
        circleClass.slice(circleClass.search(regExp)).slice(0, lastIndex),
      );
      /* Deletes the selected line from the data */
      _this.trendLineData = _this.trendLineData.filter(
        ({ ...rest }) => rest.id !== id,
      );
      gTrendLine.remove();
    }
  });

  displayDataTA(_this.candleData[_this.candleData.length - 1], _this);
  select(".hoverParent" + auxName + _this.chartName).call(
    _this.zoomTA.transform,
    _this.transform,
  );

  const yInit = _this.svgNode.y.copy();

  function draw() {
    // cursor drag & drop (north-south resize)
    _this.svgNode.gYRect.style("cursor", "ns-resize");
    // update y-axis
    _this.svgNode.gY
      .transition()
      .call(_this.svgNode.yAxis.scale(_this.svgNode.y));
  }
  function zoomedY() {
    // update y-axis scale
    _this.svgNode.y.domain(event.transform.rescaleY(yInit).domain());
    _this.svgNode.gY.call(
      _this.svgNode.yAxis.scale(event.transform.rescaleY(_this.svgNode.y)),
    );
    _this.svgNode.y = event.transform.rescaleY(_this.svgNode.y);

    draw();

    // update line and area path
    const xt = _this.transform.rescaleX(_this.svgNode.x);
    // trend line
    updateTrendLine(_this, xt, _this.candleData);

    TACandlesZooming(_this.transform, xt, _this);
    TACandlesZoomEnd(
      _this.transform,
      xt,
      _this.svgNode.y,
      _this.filteredData,
      _this,
      valueLast,
      lastYTooltip,
      upColor,
      dnColor,
      opacityUp,
      opacityDn,
      upColorBackground,
      dnColorBackground,
      barWidth,
    );
  }
  const xt = _this.transform.rescaleX(_this.svgNode.x);
  updateTrendLine(_this, xt, _this.candleData);
  const zoomY = zoom().on("zoom", zoomedY);
  _this.svgNode.gYRect.call(zoomY);
}

export function TACandlesZooming(t, xt, _this, barWidth = 0.7) {
  let bandWidth = t.k * _this.bandWidth;
  const candleWidth = bandWidth * barWidth + 1;
  let area;
  // sticks and candles
  var line = lineD3()
    .curve(curveLinear) // .curve(curveMonotoneX)
    .defined((d) => !Number.isNaN(d.y))
    .x((d) => xt(d.date))
    .y((d) => _this.svgNode.y(d.y));
  _this.svgNode["stick" + _this.chartName].attr("d", (d) => line(d.values));
  // candles
  _this.svgNode["candle" + _this.chartName]
    .attr("x", function(d) {
      return xt(d.date) - candleWidth / 2;
    })
    .attr("width", candleWidth);
  // price line
  fieldName = "close";
  line = lineD3()
    .defined((d) => !Number.isNaN(d[fieldName]))
    .x((d) => xt(d.date))
    .y((d) => _this.svgNode.y(d[fieldName]));
  _this.svgNode["upperLineTA" + _this.chartName].attr("d", (d) => line(d));
  // price line aux
  fieldName = "zero";
  line = lineD3()
    .defined((d) => !Number.isNaN(d[fieldName]))
    .x((d) => xt(d.date))
    .y((d) => _this.svgNode.y(d[fieldName]));
  _this.svgNode["lowerLineTA" + _this.chartName].attr("d", (d) => line(d));
  // price line area
  area = areaD3()
    .defined((d) => !Number.isNaN(d["close"]))
    .defined((d) => !Number.isNaN(d["zero"]))
    .x((d) => xt(d.date))
    .y0((d) => _this.svgNode.y(d["close"]))
    .y1((d) => _this.svgNode.y(d["zero"]));
  _this.svgNode["areaTA" + _this.chartName].attr("d", area);

  // bollinger
  let fieldName;
  fieldName = "bband_u2";
  line = lineD3()
    .defined((d) => !Number.isNaN(d[fieldName]))
    .x((d) => xt(d.date))
    .y((d) => _this.svgNode.y(d[fieldName]));
  _this.svgNode["upperLineBoll" + _this.chartName].attr("d", (d) => line(d));
  fieldName = "bband_u1";
  line = lineD3()
    .defined((d) => !Number.isNaN(d[fieldName]))
    .x((d) => xt(d.date))
    .y((d) => _this.svgNode.y(d[fieldName]));
  _this.svgNode["upperLineBoll2" + _this.chartName].attr("d", (d) => line(d));
  fieldName = "bband_l2";
  line = lineD3()
    .defined((d) => !Number.isNaN(d[fieldName]))
    .x((d) => xt(d.date))
    .y((d) => _this.svgNode.y(d[fieldName]));
  _this.svgNode["lowerLineBoll" + _this.chartName].attr("d", (d) => line(d));
  fieldName = "bband_l1";
  line = lineD3()
    .defined((d) => !Number.isNaN(d[fieldName]))
    .x((d) => xt(d.date))
    .y((d) => _this.svgNode.y(d[fieldName]));
  _this.svgNode["lowerLineBoll2" + _this.chartName].attr("d", (d) => line(d));
  area = areaD3()
    .defined((d) => !Number.isNaN(d["bband_u1"]))
    .defined((d) => !Number.isNaN(d["bband_l1"]))
    .x((d) => xt(d.date))
    .y0((d) => _this.svgNode.y(d["bband_u1"]))
    .y1((d) => _this.svgNode.y(d["bband_l1"]));
  _this.svgNode["areaBoll2" + _this.chartName].attr("d", area);
  fieldName = "bband_ma";
  line = lineD3()
    .defined((d) => !Number.isNaN(d[fieldName]))
    .x((d) => xt(d.date))
    .y((d) => _this.svgNode.y(d[fieldName]));
  _this.svgNode["lineBoll" + _this.chartName].attr("d", (d) => line(d));

  // ma's
  fieldName = "ma20";
  line = lineD3()
    .defined((d) => !Number.isNaN(d[fieldName]))
    .x((d) => xt(d.date))
    .y((d) => _this.svgNode.y(d[fieldName]));
  _this.svgNode["lineMA20" + _this.chartName].attr("d", (d) => line(d));

  fieldName = "ma40";
  line = lineD3()
    .defined((d) => !Number.isNaN(d[fieldName]))
    .x((d) => xt(d.date))
    .y((d) => _this.svgNode.y(d[fieldName]));
  _this.svgNode["lineMA40" + _this.chartName].attr("d", (d) => line(d));

  fieldName = "ma3";
  line = lineD3()
    .defined((d) => !Number.isNaN(d[fieldName]))
    .x((d) => xt(d.date))
    .y((d) => _this.svgNode.y(d[fieldName]));
  _this.svgNode["lineMA3" + _this.chartName].attr("d", (d) => line(d));

  // Fibonacci
  if (_this.showFibo === true && _this.fibonacci != null) {
    // Fibonacci E
    if (_this.showFiboR === true) {
      for (let i = 0; i < _this.pxPercent.length - 1; i++) {
        fieldName = "p" + (i + 1);
        line = lineD3()
          .defined((d) => !Number.isNaN(d[fieldName]))
          .x((d) => xt(d.date))
          .y((d) => _this.svgNode.y(d[fieldName]));
        _this.svgNode["fiboR" + _this.chartName + "Line" + i].attr("d", (d) =>
          line(d),
        );
        area = areaD3()
          .defined((d) => !Number.isNaN(d["p" + (i + 1)]))
          .defined((d) => !Number.isNaN(d["p" + (i + 2)]))
          .x((d) => xt(d.date))
          .y0((d) => _this.svgNode.y(d["p" + (i + 1) + _this.chartName]))
          .y1((d) => _this.svgNode.y(d["p" + (i + 2) + _this.chartName]));
        _this.svgNode["fiboR" + _this.chartName + "Area" + i].attr("d", area);
        if (i === _this.pxPercent.length - 2) {
          fieldName = "p" + (i + 2);
          line = lineD3()
            .defined((d) => !Number.isNaN(d[fieldName]))
            .x((d) => xt(d.date))
            .y((d) => _this.svgNode.y(d[fieldName]));
          _this.svgNode["fiboR" + _this.chartName + "Line" + (i + 1)].attr(
            "d",
            (d) => line(d),
          );
        }
      }

      // "FiboRTxt" + this.chartName
      _this.svgNode["FiboRTxt" + _this.chartName].attr("transform", function(
        d,
      ) {
        return `translate(${xt(d["date"]) - 5}, ${_this.svgNode.y(d["px"])})`;
      });
    }
    fieldName = "value";
    line = lineD3()
      .defined((d) => !Number.isNaN(d[fieldName]))
      .x((d) => xt(d.date))
      .y((d) => _this.svgNode.y(d[fieldName]));
    _this.svgNode["trendFibo" + _this.chartName].attr("d", (d) => line(d));
    let p1 = _this.fiboTrendData[0].value;
    let p2 = _this.fiboTrendData[1].value;
    let del = p1 > p2 ? -5 : 5;
    _this.svgNode["diamondFibo1"].attr(
      "transform",
      `translate(${xt(_this.fiboTrendData[0].date)},${_this.svgNode.y(p1) +
        del}) rotate(45)`,
    );
    _this.svgNode["diamondFibo2"].attr(
      "transform",
      `translate(${xt(_this.fiboTrendData[1].date)},${_this.svgNode.y(p2) -
        del}) rotate(45)`,
    );
    if (_this.showFiboE) {
      for (let i = 0; i < 6; i++) {
        fieldName = "value";
        line = lineD3()
          .defined((d) => !Number.isNaN(d[fieldName]))
          .x((d) => xt(d.date))
          .y((d) => _this.svgNode.y(d[fieldName]));
        _this.svgNode["fiboE" + _this.chartName + i].attr("d", (d) => line(d));
      }
      // "FiboETxt" + this.chartName
      _this.svgNode["FiboETxt" + _this.chartName].attr("transform", function(
        d,
      ) {
        return `translate(${xt(d["date"]) - 5}, ${_this.svgNode.y(d["px"])})`;
      });
    }
    if (_this.showFiboTE) {
      for (let i = 0; i < _this.fiboTEData.length; i++) {
        fieldName = "value";
        line = lineD3()
          .curve(curveMonotoneX)
          .defined((d) => !Number.isNaN(d["value"]))
          .x((d) => xt(d.date))
          .y((d) => (d["hei"] === 0 ? _this.svgNode.height : 0));
        _this.svgNode["fiboTE" + _this.chartName + i].attr("d", (d) => line(d));
      }
      // "FiboTETxt" + this.chartName
      _this.svgNode["FiboTETxt" + _this.chartName].attr("transform", function(
        d,
      ) {
        return `translate(${xt(d["date"])}, ${
          _this.svgNode.height
        }) rotate(${90})`;
      });
    }
  }
  // trend line
  const xt1 = _this.transform.rescaleX(_this.svgNode.x);
  updateTrendLine(_this, xt1, _this.candleData);
}

export function TACandlesZoomEnd(
  t,
  xt,
  yt,
  filtered,
  _this,
  valueLast,
  lastYTooltip,
  upColor,
  dnColor,
  opacityUp = 1,
  opacityDn = 1,
  upColorBackground = null,
  dnColorBackground = null,
  barWidth = 0.7,
) {
  if (upColorBackground === null) {
    upColorBackground = upColor;
  }
  if (dnColorBackground === null) {
    dnColorBackground = dnColor;
  }
  let bandWidth = t.k * _this.bandWidth;
  const candleWidth = bandWidth * barWidth + 1;

  // candles
  _this.svgNode["candle" + _this.chartName] = _this.svgNode[
    "candle" + _this.chartName
  ]
    .data(filtered, (d) => d.id)
    .join(
      function(enter) {
        return enter
          .append("rect")
          .attr("x", (d) => xt(d.date) - candleWidth / 2)
          .attr("width", candleWidth)
          .attr("y", (d) => yt(Math.max(d.open, d.close)))
          .attr("height", (d) =>
            Math.max(
              1,
              yt(Math.min(d.open, d.close)) - yt(Math.max(d.open, d.close)),
            ),
          )
          .attr("stroke", function(d) {
            return d.close >= d.open ? upColor : dnColor;
          })
          .style("fill", function(d) {
            return d.close >= d.open ? upColorBackground : dnColorBackground;
          })
          .attr("fill-opacity", function(d) {
            return d.close > d.open ? opacityUp : opacityDn;
          })
          .style("shape-rendering", "crispEdges")
          .style("vector-effect", "non-scaling-stroke");
      },
      function(update) {
        return update
          .attr("x", (d) => xt(d.date) - candleWidth / 2)
          .attr("width", candleWidth)
          .attr("y", (d) => yt(Math.max(d.open, d.close)))
          .attr("height", (d) =>
            Math.max(
              1,
              yt(Math.min(d.open, d.close)) - yt(Math.max(d.open, d.close)),
            ),
          )
          .style("fill", function(d) {
            return d.close >= d.open ? upColorBackground : dnColorBackground;
          })
          .attr("stroke", function(d) {
            return d.close >= d.open ? upColor : dnColor;
          })
          .style("shape-rendering", "crispEdges")
          .style("vector-effect", "non-scaling-stroke");
      },
      function(exit) {
        return exit.remove();
      },
    );

  // sticks
  let newData = dataForSticks(filtered);
  var line = lineD3()
    .curve(curveLinear)
    .defined((d) => !Number.isNaN(d.y))
    .x((d) => xt(d.date))
    .y((d) => _this.svgNode.y(d.y));
  _this.svgNode["stick" + _this.chartName] = _this.svgNode[
    "stick" + _this.chartName
  ]
    .data(newData.series, (d) => d.id)
    .join("path")
    .style("stroke", (d) => (d.name === "up" ? upColor : dnColor))
    .attr("d", (d) => line(d.values))
    .style("shape-rendering", "crispEdges")
    .style("vector-effect", "non-scaling-stroke");
  // line price
  fieldName = "close";
  line = lineD3()
    .defined((d) => !Number.isNaN(d[fieldName]))
    .x((d) => xt(d.date))
    .y((d) => _this.svgNode.y(d[fieldName]));
  _this.svgNode["upperLineTA" + _this.chartName]
    .data([filtered])
    .join("path")
    .attr("d", (d) => line(d));
  fieldName = "zero";
  line = lineD3()
    .defined((d) => !Number.isNaN(d[fieldName]))
    .x((d) => xt(d.date))
    .y((d) => _this.svgNode.y(d[fieldName]));
  _this.svgNode["lowerLineTA" + _this.chartName]
    .data([filtered])
    .join("path")
    .attr("d", (d) => line(d));
  area = areaD3()
    .defined((d) => !Number.isNaN(d["close"]))
    .defined((d) => !Number.isNaN(d["zero"]))
    .x((d) => xt(d.date))
    .y0((d) => _this.svgNode.y(d["close"]))
    .y1((d) => _this.svgNode.y(d["zero"]));
  _this.svgNode["areaTA" + _this.chartName]
    .data([filtered])
    .join("path")
    .attr("d", area);
  // what to show
  if (!_this.isCandles) {
    _this.svgNode["stick" + _this.chartName].style("opacity", 0);
    _this.svgNode["candle" + _this.chartName].style("opacity", 0);
  } else {
    _this.svgNode["upperLineTA" + _this.chartName].style("opacity", 0);
    _this.svgNode["lowerLineTA" + _this.chartName].style("opacity", 0);
    _this.svgNode["areaTA" + _this.chartName].style("opacity", 0);
  }

  // 20 ma
  let fieldName;
  fieldName = "ma20";
  line = lineD3()
    .defined((d) => !Number.isNaN(d[fieldName]))
    .x((d) => xt(d.date))
    .y((d) => _this.svgNode.y(d[fieldName]));
  _this.svgNode["lineMA20" + _this.chartName]
    .data([filtered])
    .join("path")
    .attr("d", (d) => line(d));
  // 40 ma
  fieldName = "ma40";
  line = lineD3()
    .defined((d) => !Number.isNaN(d[fieldName]))
    .x((d) => xt(d.date))
    .y((d) => _this.svgNode.y(d[fieldName]));
  _this.svgNode["lineMA40" + _this.chartName]
    .data([filtered])
    .join("path")
    .attr("d", (d) => line(d));
  // 3 ma
  fieldName = "ma3";
  line = lineD3()
    .defined((d) => !Number.isNaN(d[fieldName]))
    .x((d) => xt(d.date))
    .y((d) => _this.svgNode.y(d[fieldName]));
  _this.svgNode["lineMA3" + _this.chartName]
    .data([filtered])
    .join("path")
    .attr("d", (d) => line(d));
  let area;
  // bollinger
  fieldName = "bband_u2";
  line = lineD3()
    .defined((d) => !Number.isNaN(d[fieldName]))
    .x((d) => xt(d.date))
    .y((d) => _this.svgNode.y(d[fieldName]));
  _this.svgNode["upperLineBoll" + _this.chartName]
    .data([filtered])
    .join("path")
    .attr("d", (d) => line(d));
  fieldName = "bband_u1";
  line = lineD3()
    .defined((d) => !Number.isNaN(d[fieldName]))
    .x((d) => xt(d.date))
    .y((d) => _this.svgNode.y(d[fieldName]));
  _this.svgNode["upperLineBoll2" + _this.chartName]
    .data([filtered])
    .join("path")
    .attr("d", (d) => line(d));
  fieldName = "bband_l2";
  line = lineD3()
    .defined((d) => !Number.isNaN(d[fieldName]))
    .x((d) => xt(d.date))
    .y((d) => _this.svgNode.y(d[fieldName]));
  _this.svgNode["lowerLineBoll" + _this.chartName]
    .data([filtered])
    .join("path")
    .attr("d", (d) => line(d));
  fieldName = "bband_l1";
  line = lineD3()
    .defined((d) => !Number.isNaN(d[fieldName]))
    .x((d) => xt(d.date))
    .y((d) => _this.svgNode.y(d[fieldName]));
  _this.svgNode["lowerLineBoll2" + _this.chartName]
    .data([filtered])
    .join("path")
    .attr("d", (d) => line(d));
  area = areaD3()
    .defined((d) => !Number.isNaN(d["bband_u1"]))
    .defined((d) => !Number.isNaN(d["bband_l1"]))
    .x((d) => xt(d.date))
    .y0((d) => _this.svgNode.y(d["bband_u1"]))
    .y1((d) => _this.svgNode.y(d["bband_l1"]));
  _this.svgNode["areaBoll2" + _this.chartName]
    .data([filtered])
    .join("path")
    .attr("d", area);
  fieldName = "bband_ma";
  line = lineD3()
    .defined((d) => !Number.isNaN(d[fieldName]))
    .x((d) => xt(d.date))
    .y((d) => _this.svgNode.y(d[fieldName]));
  _this.svgNode["lineBoll" + _this.chartName]
    .data([filtered])
    .join("path")
    .attr("d", (d) => line(d));
  // Fibonacci
  if (_this.showFibo === true && _this.fibonacci != null) {
    // Fibonacci R
    if (_this.showFiboR === true) {
      for (let i = 0; i < _this.pxPercent.length - 1; i++) {
        fieldName = "p" + (i + 1);
        line = lineD3()
          .defined((d) => !Number.isNaN(d[fieldName]))
          .x((d) => xt(d.date))
          .y((d) => _this.svgNode.y(d[fieldName]));
        _this.svgNode["fiboR" + _this.chartName + "Line" + i].attr("d", (d) =>
          line(d),
        );
        area = areaD3()
          .defined((d) => !Number.isNaN(d["p" + (i + 1)]))
          .defined((d) => !Number.isNaN(d["p" + (i + 2)]))
          .x((d) => xt(d.date))
          .y0((d) => _this.svgNode.y(d["p" + (i + 1) + _this.chartName]))
          .y1((d) => _this.svgNode.y(d["p" + (i + 2) + _this.chartName]));
        _this.svgNode["fiboR" + _this.chartName + "Area" + i].attr("d", area);
        if (i === _this.pxPercent.length - 2) {
          fieldName = "p" + (i + 2);
          line = lineD3()
            .defined((d) => !Number.isNaN(d[fieldName]))
            .x((d) => xt(d.date))
            .y((d) => _this.svgNode.y(d[fieldName]));
          _this.svgNode["fiboR" + _this.chartName + "Line" + (i + 1)].attr(
            "d",
            (d) => line(d),
          );
        }
      }
      // "FiboRTxt" + this.chartName
      _this.svgNode["FiboRTxt" + _this.chartName].attr("transform", function(
        d,
      ) {
        return `translate(${xt(d["date"]) - 5}, ${_this.svgNode.y(d["px"])})`;
      });
    }
    fieldName = "value";
    line = lineD3()
      .defined((d) => !Number.isNaN(d[fieldName]))
      .x((d) => xt(d.date))
      .y((d) => _this.svgNode.y(d[fieldName]));
    _this.svgNode["trendFibo" + _this.chartName].attr("d", (d) => line(d));
    let p1 = _this.fiboTrendData[0].value;
    let p2 = _this.fiboTrendData[1].value;
    let del = p1 > p2 ? -5 : 5;
    _this.svgNode["diamondFibo1"].attr(
      "transform",
      `translate(${xt(_this.fiboTrendData[0].date)},${_this.svgNode.y(p1) +
        del}) rotate(45)`,
    );
    _this.svgNode["diamondFibo2"].attr(
      "transform",
      `translate(${xt(_this.fiboTrendData[1].date)},${_this.svgNode.y(p2) -
        del}) rotate(45)`,
    );
    // Fibonacci E
    if (_this.showFiboE) {
      for (let i = 0; i < 6; i++) {
        fieldName = "value";
        line = lineD3()
          .defined((d) => !Number.isNaN(d[fieldName]))
          .x((d) => xt(d.date))
          .y((d) => _this.svgNode.y(d[fieldName]));
        _this.svgNode["fiboE" + _this.chartName + i].attr("d", (d) => line(d));
      }
      // "FiboETxt" + this.chartName
      _this.svgNode["FiboETxt" + _this.chartName].attr("transform", function(
        d,
      ) {
        return `translate(${xt(d["date"]) - 5}, ${_this.svgNode.y(d["px"])})`;
      });
    }
    // Fibonacci TE
    if (_this.showFiboTE) {
      for (let i = 0; i < _this.fiboTEData.length; i++) {
        fieldName = "value";
        line = lineD3()
          .curve(curveMonotoneX)
          .defined((d) => !Number.isNaN(d["value"]))
          .x((d) => xt(d.date))
          .y((d) => (d["hei"] === 0 ? _this.svgNode.height : 0));
        _this.svgNode["fiboTE" + _this.chartName + i].attr("d", (d) => line(d));
      }
      // "FiboTETxt" + this.chartName
      _this.svgNode["FiboTETxt" + _this.chartName].attr("transform", function(
        d,
      ) {
        return `translate(${xt(d["date"])}, ${
          _this.svgNode.height
        }) rotate(${90})`;
      });
    }
  }

  // last rate
  var yMax = yt.invert(0);
  var yMin = yt.invert(_this.svgNode.height);
  lastYTooltip
    .html(format(_this.currencyFormat)(valueLast))
    .transition()
    .duration(500)
    .style("opacity", yMin <= valueLast && valueLast <= yMax ? 1 : 0)
    .style("top", yt(valueLast) + _this.svgNode.margin.top - 11 + "px");

  displayDataTA(filtered.at(-1), _this);
  // y axis
  let tickCount = _this.svgNode.height / 40;
  _this.svgNode.gY
    .transition()
    .duration(500)
    .call(
      axisRight()
        .tickSize(-_this.svgNode.width)
        .ticks(tickCount)
        .tickSizeOuter(0)
        .tickPadding(10)
        .scale(_this.svgNode.y),
    );
  // trendLine
  const xt1 = _this.transform.rescaleX(_this.svgNode.x);
  updateTrendLine(_this, xt1, _this.candleData);
}

export function inRange(d, start, end) {
  return Number.isFinite((d = d.valueOf())) &&
    Number.isFinite((start = start.valueOf())) &&
    Number.isFinite((end = end.valueOf()))
    ? start <= d && d <= end
    : NaN;
}

export function dataForSticks(data) {
  let series1 = data.map(function(d) {
    return {
      name: d.close >= d.open ? "up" : "dn",
      id: +d.date,
      values: [{ date: d.date, y: d.high }].concat([
        { date: d.date, y: Math.max(d.open, d.close) },
      ]),
    };
  });
  let series2 = data.map(function(d) {
    return {
      name: d.close >= d.open ? "up" : "dn",
      id: +d.date + 0.5,
      values: [{ date: d.date, y: d.low }].concat([
        { date: d.date, y: Math.min(d.open, d.close) },
      ]),
    };
  });
  return {
    name: "sticks",
    series: series1.concat(series2),
  };
}

export function roundDate(timeStamp) {
  timeStamp -= timeStamp % (24 * 60 * 60 * 1000); //subtract amount of time since midnight
  timeStamp += new Date().getTimezoneOffset() * 60 * 1000; //add on the timezone offset
  return new Date(timeStamp);
}

export function displayDataTA(d, _this) {
  if (d === undefined) {
    return;
  }
  _this.priceLegend = {
    date: d.date,
    open: d.open,
    high: d.high,
    low: d.low,
    close: d.close,
    change: d.close / d.open - 1,
  };
  if (_this.show20ma) {
    select("#infoma20TA").text(format(_this.currencyFormat)(d.ma20));
  }
  if (_this.show40ma) {
    select("#infoma40TA").text(format(_this.currencyFormat)(d.ma40));
  }
  if (_this.show3ma) {
    select("#infoma3TA").text(format(_this.currencyFormat)(d.ma3));
  }
  if (_this.showBoll) {
    select("#infobollupTA").text(format(_this.currencyFormat)(d.bband_u1));
    select("#infobolldownTA").text(format(_this.currencyFormat)(d.bband_l1));
  }
  if (_this.showRSI) {
    select("#inforsiTA").text(format(_this.currencyFormat)(d.rsi));
  }
  if (_this.showMACD) {
    select("#infomacdTA").text(format(_this.currencyFormat)(d.macd));
    select("#infomacdSignalTA").text(
      format(_this.currencyFormat)(d.macdSignal),
    );
    select("#infomacdHistogramTA").text(
      format(_this.currencyFormat)(d.macdHist),
    );
  }
  if (_this.showSTOCH) {
    select("#infostochPKTA").text(format(_this.currencyFormat)(d.stochPK));
    select("#infostochPDTA").text(format(_this.currencyFormat)(d.stochPD));
  }
}

export function mouseDate(x0, data, date = "date") {
  var date_max = data[data.length - 1][date];
  var bisectDate = bisector(function(d) {
    return d[date];
  }).left;
  let i = bisectDate(data, x0, 1);
  let d0 = data[i - 1];
  let d;
  if (d0[date] === date_max) {
    d = d0;
  } else {
    var d1 = data[i];
    d = x0 - d0[date] > d1[date] - x0 ? d1 : d0;
  }
  return d;
}

/**
 * creates x and y tooltips
 * @param {object} svg: svg variable
 * @param {function} currencyFormat: format the y data
 * @param {double} valueLast: latest y value
 * @param {double} xTooltipWidth: x axis Tooltip Width
 * @param {boolean} colorTheme: true dark, false light
 * @param {string} svgName: name auxiliary of svg
 * @param {function} colorLastTooltip: color function
 * @param {function} colorXTooltip: color function
 * @param {function} colorYTooltip: color function
 */
export function createTooltips(
  svg,
  currencyFormat,
  valueLast,
  xTooltipWidth = 80,
  colorTheme,
  svgName,
  colorLastTooltip = null,
  colorXTooltip = null,
  colorYTooltip = null,
  yTooltipWidth = 60,
) {
  if (colorLastTooltip == null) {
    colorLastTooltip = colorDarkExpo;
  }
  if (colorXTooltip == null) {
    colorXTooltip = colorDarkExpo;
  }
  if (colorYTooltip == null) {
    colorYTooltip = lightbackground2;
  }
  let sharp = "#";
  select(`${sharp}${svg.parentElement}`)
    .selectAll(`.tooltip${svgName}`)
    .remove();
  let lastYTooltip;
  if (valueLast) {
    lastYTooltip = select(`${sharp}${svg.parentElement}`)
      .append("div")
      .style("opacity", 1)
      .style("margin", "0 auto")
      .style("text-align", "center")
      .style("background", colorDarkExpo(colorTheme))
      .style("border", colorDarkExpo(colorTheme))
      .style("color", textactive2(colorTheme))
      .style("padding", "2px")
      .attr("class", `tooltip tooltip${svgName}`)
      .style("font-size", "0.8rem")
      .style("width", "60px")
      .style("left", svg.width + svg.margin.left + 1 + "px")
      .style("top", svg.y(valueLast) + svg.margin.top - 11 + "px")
      .html(format(currencyFormat)(valueLast));
  }
  let yTooltip = select(`${sharp}${svg.parentElement}`)
    .append("div")
    .style("opacity", 0)
    .style("margin", "0 auto")
    .style("text-align", "center")
    .style("background", colorYTooltip(colorTheme))
    .style("border", colorYTooltip(colorTheme))
    .style("color", textactive2(colorTheme))
    .style("padding", "2px")
    .attr("class", `tooltip tooltip${svgName}`)
    .style("font-size", "0.8rem")
    .style("width", `${yTooltipWidth}px`)
    .html("");
  let xTooltip = select(`${sharp}${svg.parentElement}`)
    .append("div")
    .style("opacity", 0)
    .style("margin", "0 auto")
    .style("text-align", "center")
    .style("background", colorXTooltip(colorTheme))
    .style("border", colorXTooltip(colorTheme))
    .style("color", textactive2(colorTheme))
    .style("padding", "2px")
    .attr("class", `tooltip tooltip${svgName}`)
    .style("font-size", "0.8rem")
    .style("width", xTooltipWidth + "px")
    .html("");
  return { yTooltip, lastTooltip: lastYTooltip, xTooltip };
}

export function zoomSetup(
  _this,
  svgName,
  zoomName,
  auxName,
  chartName,
  mouseover,
  mouseout,
  zoomed,
  zoomEnd,
) {
  if (zoomed) {
    _this[zoomName] = zoom()
      .scaleExtent([1, 100])
      .extent([
        [0, 0],
        [_this[svgName].width, _this[svgName].height],
      ])
      .translateExtent([
        [0, 0],
        [_this[svgName].width, _this[svgName].height],
      ])
      .on("zoom", zoomed)
      .on("zoom.end", zoomEnd);
  }
  _this[svgName]
    .append("defs")
    .append("clipPath")
    .attr("id", "clip" + auxName + chartName)
    .append("rect")
    .attr("width", _this[svgName].width)
    .attr("height", _this[svgName].height);

  var hoverGroup = _this[svgName]
    .append("g")
    .attr("class", "hoverParent" + auxName + chartName)
    .attr("clip-path", `url(#clip${auxName + chartName})`);
  hoverGroup
    .append("rect")
    .attr("width", _this[svgName].width)
    .attr("height", _this[svgName].height)
    .style("fill", "rgba(0,0,0,0)")
    .on("mouseover", mouseover)
    .on("mouseover", mouseover)
    .on("mousemove", mouseover)
    .on("mouseout", mouseout);
  if (zoomed) {
    select(".hoverParent" + auxName + chartName).call(_this[zoomName]);
  }
}

export function create2YTooltips(
  svg,
  currencyFormat,
  valueLast,
  xTooltipWidth = 80,
  y1Color = "white",
  y2Color = "white",
  colorTheme,
  yTooltipWidth = 60,
) {
  let sharp = "#";
  select(`${sharp}${svg.parentElement}`)
    .selectAll(".tooltip")
    .remove();
  let yTooltip = select(`${sharp}${svg.parentElement}`)
    .append("div")
    .style("opacity", 0)
    .style("margin", "0 auto")
    .style("text-align", "center")
    .style("background", y1Color)
    .style("border", y1Color)
    .style("color", textactive2(colorTheme))
    .style("padding", "2px")
    .attr("class", "tooltip")
    .style("font-size", "0.8rem")
    .style("width", `${yTooltipWidth}px`)
    .html("");
  let y2Tooltip = select(`${sharp}${svg.parentElement}`)
    .append("div")
    .style("opacity", 0)
    .style("margin", "0 auto")
    .style("text-align", "center")
    .style("background", y2Color)
    .style("border", y2Color)
    .style("color", textactive2(colorTheme))
    .style("padding", "2px")
    .attr("class", "tooltip")
    .style("font-size", "0.8rem")
    .style("width", `${yTooltipWidth}px`)
    .html("");
  let xTooltip = select(`${sharp}${svg.parentElement}`)
    .append("div")
    .style("opacity", 0)
    .style("margin", "0 auto")
    .style("text-align", "center")
    .style("background", colorDarkExpo(colorTheme))
    .style("border", colorDarkExpo(colorTheme))
    .style("color", textactive2(colorTheme))
    .style("padding", "2px")
    .attr("class", "tooltip")
    .style("font-size", "0.8rem")
    .style("width", xTooltipWidth + "px")
    .html("");
  return { yTooltip: yTooltip, y2Tooltip: y2Tooltip, xTooltip: xTooltip };
}

/* Update trend line with respect to rescaling */
function updateTrendLine(_this, xt, data) {
  const yt = _this.svgNode.y;
  const straightLine = lineD3()
    .x((d) => xt(d.date))
    .y((d) => yt(d.close));

  const updateCircle = (id, position, d, classed) => {
    select(`.circle-${classed}${id}`)
      .attr("cx", () => xt(d[position].date))
      .attr("cy", () => yt(d[position].close));
  };

  if (!selectAll(".trendLine").empty()) {
    const dataFilter = filterTrendLineData(_this);

    dataFilter.forEach((d) => {
      const [start, end] = Object.keys(d);
      const id = start.slice(start.search(/[0-9]/));

      const startDate = d[start].date;
      const endDate = d[end].date;
      const startClose = d[start].close;
      const endClose = d[end].close;
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);
      _this.additionalDates.map((d) => d.date.setHours(0, 0, 0, 0));
      const copiedData = [...data].concat(..._this.additionalDates);

      const filteredData = copiedData.filter((d) => {
        const date = new Date(d.date);
        if (startDate < endDate) {
          return date >= startDate && date <= endDate;
        } else {
          return date >= endDate && date <= startDate;
        }
      });

      const numDataPoints = filteredData.length;
      filteredData.sort((a, b) => +a.date - +b.date);

      const addFilterData = filteredData.map((d, i) => {
        const interpolationFactor = i / (numDataPoints - 1);

        let close;
        let date;
        if (i === 0 && +d.date !== +startDate) {
          date = startDate;
          close = startClose;
        } else if (i === filteredData.length - 1 && +d.date !== +endDate) {
          date = endDate;
          close = endClose;
        } else {
          date = d.date;
          if (startDate < endDate) {
            close = startClose + (endClose - startClose) * interpolationFactor;
          } else {
            close =
              startClose + (endClose - startClose) * (1 - interpolationFactor);
          }
        }
        return { close, date };
      });

      addFilterData.sort((a, b) => +a.date - +b.date);

      select(`.trendLine${id}`)
        .data([addFilterData])
        .join("path")
        .attr("d", (d) => straightLine(d));

      updateCircle(id, start, d, "start");
      updateCircle(id, end, d, "end");
    });
  }
}
